import React, { useEffect, useState } from "react";
import { EnergyData, Resolution, TimePeriod } from "../types";
import { getProjectEnergy, getSiteEnergy, getProjectWithGenerationSummary } from "../api";
import DataSummary from "./DataSummary";
import TimePeriodSelector from "./TimePeriodSelector";
import { getAvailableResolutions, isSite, isProject } from "../utils";
import { InstanceWithGenerationSummary, SiteWithGenerationSummary, ProjectWithGenerationSummary } from "../../../types";
import { startOfYesterday } from "date-fns";
import { ClockIcon, ArrowPathIcon } from "@heroicons/react/24/outline";

const ScheduleInfo = ({ instanceWithGenerationSummary }: { instanceWithGenerationSummary: SiteWithGenerationSummary }) => {
    return (
        <div className="flex justify-start items-center gap-2">
            { instanceWithGenerationSummary.instance.timezone && <div className="flex justify-start items-center gap-1">
                <ClockIcon className="h-4 w-4 text-pacificblue-950 text-sm" />
                <p className="text-gray-500 text-sm">{instanceWithGenerationSummary.instance.timezone}</p>
            </div> }
            { instanceWithGenerationSummary.schedule && instanceWithGenerationSummary.schedule.crontab && instanceWithGenerationSummary.schedule.crontab.human_readable &&
            <div className="flex justify-start items-center gap-1">
                <ArrowPathIcon className="h-4 w-4 text-pacificblue-950 text-sm" />
                <p className="text-gray-500 text-sm">Update every {instanceWithGenerationSummary.schedule.crontab.human_readable}</p>
            </div> }
        </div>
    );
}   

interface OperationalDataProps {
    instanceWithGenerationSummary: InstanceWithGenerationSummary;
    initPeriod: TimePeriod;
    filteredSiteIds?: number[];
    onLoadingChange?: (loading: boolean) => void;
}

const OperationalData: React.FC<OperationalDataProps> = ({ instanceWithGenerationSummary, initPeriod, filteredSiteIds, onLoadingChange }) => {
    const [period, setPeriod] = useState<TimePeriod>(initPeriod);
    const [resolution, setResolution] = useState<Resolution>(Resolution.Mins);
    const [date, setDate] = useState<Date>(startOfYesterday());
    const [energyData, setEnergyData] = useState<EnergyData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [filteredSummary, setFilteredSummary] = useState<ProjectWithGenerationSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState<boolean>(false);

    // Notify parent of loading state changes
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    // Stringify site IDs for stable dependency comparison
    const siteIdsKey = filteredSiteIds?.join(',') ?? '';

    // Fetch filtered summary when site filter changes (for projects only)
    useEffect(() => {
        if (isProject(instanceWithGenerationSummary.instance) && filteredSiteIds && filteredSiteIds.length > 0) {
            setSummaryLoading(true);
            getProjectWithGenerationSummary(
                instanceWithGenerationSummary.instance.id.toString(),
                filteredSiteIds
            ).then(summary => {
                setFilteredSummary(summary);
                setSummaryLoading(false);
            }).catch(err => {
                console.error('Failed to fetch filtered summary:', err);
                setFilteredSummary(null);
                setSummaryLoading(false);
            });
        } else {
            setFilteredSummary(null);
            setSummaryLoading(false);
        }
    }, [siteIdsKey]);

    useEffect(() => {
        if (period && resolution && date) {
            // Set resolution to the finest available resolution if the selected resolution is not available
            let tempResolution = resolution;
            if (!getAvailableResolutions(period).includes(resolution)) {
                tempResolution = getAvailableResolutions(period)[0];
                setResolution(tempResolution);
                return;
            }
            // Get the energy data
            setLoading(true);
            if (isSite(instanceWithGenerationSummary.instance)) {
                getSiteEnergy(instanceWithGenerationSummary.instance.id.toString(), period, tempResolution, date).then((data) => {
                    setEnergyData(data);
                    setLoading(false);
                }).catch((err) => {
                    setLoading(false);
                });
            } else {
                // If filteredSiteIds is defined and empty, no sites match - show no data
                // If undefined, fetch all (no filter applied)
                if (Array.isArray(filteredSiteIds) && filteredSiteIds.length === 0) {
                    setEnergyData(null);
                    setLoading(false);
                    return;
                }
                getProjectEnergy(instanceWithGenerationSummary.instance.id.toString(), period, tempResolution, date, filteredSiteIds).then((data) => {
                    setEnergyData(data);
                    setLoading(false);
                }).catch((err) => {
                    setEnergyData(null);
                    setLoading(false);
                });
            }
        }
    }, [period, resolution, date, siteIdsKey]);

    return (
        <div>
            <span className="flex justify-start items-center gap-2 my-2">
                <h2 className="text-xl font-bold text-pacificblue-950">Operational Data</h2> {isSite(instanceWithGenerationSummary.instance) && <ScheduleInfo instanceWithGenerationSummary={instanceWithGenerationSummary as SiteWithGenerationSummary} />}
            </span>
            <div className="bg-pacificblue-950 text-white rounded-2xl p-4 flex flex-col gap-4 justify-between">
                <TimePeriodSelector loading={loading || !energyData} period={period} setPeriod={setPeriod} resolution={resolution} setResolution={setResolution} date={date} setDate={setDate} />
                <DataSummary
                    instanceWithGenerationSummary={filteredSummary || instanceWithGenerationSummary}
                    energyData={energyData}
                    loading={loading}
                    summaryLoading={summaryLoading}
                />
            </div>
        </div>
    )
}

export default OperationalData;